/**
 * GFG-273 (Campaigns tab, GFG Admin) — the confirmation in front of removing a recipient from a
 * campaign, opened by the contacts table's X icon.
 *
 * `GfgModal` was considered and not used: its primary button is always live, and this confirm has
 * to be able to refuse. A contact who already redeemed never reaches here (the X that opens this
 * is disabled for them), so the disabled button is the second line of defence for any path that
 * gets around the first — `canRemoveContact` decides both, from one place.
 */
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Dialog } from "@mui/material";
import { type ReactNode, useId } from "react";
import { CONTACTS_FORM_PAPER_SX } from "@/components/contacts/ContactsDialog";
import GfgButton from "@/components/ui/GfgButton";
import { COPY } from "@/constants/adminCampaigns";
import { COPY as SHARED_CONTACTS_COPY } from "@/constants/campaignContacts";
import type { AdminCampaignLock } from "@/interfaces/adminCampaigns";
import { canRemoveContact } from "@/utils/adminCampaigns-helper";

interface RemoveContactModalProps {
  /** null while no row is pending removal — the dialog is closed in that state. */
  contact: { name: string; status: string } | null;
  /** The campaign's lock, straight from the contacts endpoint; never derived from its status. */
  lock: AdminCampaignLock;
  open: boolean;
  /** Cancel, Escape and a backdrop click all land here — none of them removes anything. */
  onClose: () => void;
  /** Fires only when the confirm button is live; the caller owns the request and its failure. */
  onConfirm: () => void;
  /** The caller's DELETE is in flight — the confirm cannot be pressed twice. */
  pending?: boolean;
  /**
   * The sentence in the warning box. Defaults to the admin Campaigns tab's own copy; the end-user
   * contacts modal (GFG-364) passes its own personalised sentence.
   */
  warning?: ReactNode;
  /** Heading override; defaults to the admin Campaigns tab's title. */
  title?: ReactNode;
  /** Confirm button label override; defaults to the admin Campaigns tab's label. */
  confirmLabel?: string;
}

export default function RemoveContactModal({
  contact,
  lock,
  open,
  onClose,
  onConfirm,
  pending = false,
  warning = COPY.removeContactModal.warningDelivered,
  title = COPY.removeContactModal.title,
  confirmLabel = COPY.removeContactModal.confirm,
}: RemoveContactModalProps) {
  const titleId = useId();
  const reasonId = useId();
  const removable = contact !== null && canRemoveContact(contact, lock);
  const refused = !removable || pending;
  const reason =
    contact !== null && !removable ? SHARED_CONTACTS_COPY.toast.removeRedeemed(contact.name) : null;

  function handleConfirm() {
    if (refused) return;
    onConfirm();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: CONTACTS_FORM_PAPER_SX } }}
    >
      <h2 id={titleId} className="text-[20px] font-bold text-rodeo-black">
        {title}
      </h2>

      <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-good-wine-4 bg-good-wine-bg px-3.5 py-3 text-[13px] leading-[1.5] text-good-wine">
        <WarningAmberOutlinedIcon sx={{ fontSize: 18, flexShrink: 0, mt: "1px" }} />
        <div className="flex flex-col gap-2">{warning}</div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
        <GfgButton variant="secondary" onClick={onClose}>
          {COPY.removeContactModal.cancel}
        </GfgButton>
        {/* aria-disabled, not `disabled`: the reason this confirm is refused has to stay reachable
            by keyboard, and a natively disabled button is neither focusable nor announced. */}
        <GfgButton
          variant="danger"
          onClick={handleConfirm}
          aria-disabled={refused || undefined}
          aria-describedby={reason === null ? undefined : reasonId}
          className="aria-disabled:opacity-40 aria-disabled:cursor-default"
          title={reason ?? undefined}
        >
          {confirmLabel}
        </GfgButton>
        {reason !== null && (
          <span id={reasonId} className="sr-only">
            {reason}
          </span>
        )}
      </div>
    </Dialog>
  );
}
